"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, Mail, ArrowUpRight, Instagram, Facebook, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
interface RecursiveMenuProps {
  items: MenuItem[];
  level?: number;
}
function RecursiveFooterNav({
  items,
  level = 0
}: RecursiveMenuProps) {
  if (!items || items.length === 0) return null;
  return <ul className={level === 0 ? "space-y-2.5" : "mt-2 space-y-2 pl-3 border-l border-primary/20"}>
      {items.map((item, i) => {
      const hasChildren = Boolean(item.children && item.children.length > 0);
      return <li key={item.id || `menu-item-${i}`} data-index={i} className="text-sm">
            {item.href ? <Link to={item.href} className="group inline-flex items-center text-secondary-foreground/80 hover:text-primary transition-colors duration-180">
                <span>{item.label}</span>
                <ArrowUpRight className="ml-1 size-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-180 text-primary" />
              </Link> : <span className="text-secondary-foreground/60">{item.label}</span>}

            {hasChildren && item.children && <RecursiveFooterNav items={item.children} level={level + 1} />}
          </li>;
    })}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2.5">
        <div className="h-4 w-24 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-4 w-32 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-4 w-28 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  const fallbackLinks = [{
    label: "Services",
    to: "/ServicesOverviewPage"
  }, {
    label: "Process",
    to: "/ProcessPage"
  }, {
    label: "Cost Guide",
    to: "/RemodelingCostGuidePage"
  }, {
    label: "Projects",
    to: "/OurProjectsPage"
  }, {
    label: "Service Areas",
    to: "/ServiceAreasPage"
  }, {
    label: "About",
    to: "/AboutUsOurProcessPage"
  }, {
    label: "Blog",
    to: "/BlogPage"
  }, {
    label: "FAQ",
    to: "/FaqPage"
  }, {
    label: "Contact",
    to: "/ContactRequestAnEstimatePage"
  }];
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-2.5 text-sm">
        {fallbackLinks.map((link, index) => <li key={link.label} data-index={index}>
            <Link to={link.to} className="group inline-flex items-center text-secondary-foreground/80 hover:text-primary transition-colors duration-180">
              <span>{link.label}</span>
              <ArrowUpRight className="ml-1 size-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-180 text-primary" />
            </Link>
          </li>)}
      </ul>;
  }
  return <RecursiveFooterNav items={menuItems} />;
}
export default function Footer() {
  const prefersReducedMotion = useReducedMotion();
  const serviceAreas = ["Brookhaven", "DeKalb County", "Ashford Park", "Brookhaven Heights", "Drew Valley", "Lynwood Park", "Sexton Woods"];
  const socialLinks = [{
    title: "Instagram",
    href: "#",
    icon: Instagram
  }, {
    title: "Facebook",
    href: "#",
    icon: Facebook
  }, {
    title: "LinkedIn",
    href: "#",
    icon: Linkedin
  }];
  return <section id="footer" data-nav="dark" className="w-full bg-background">
      <div className="w-full bg-secondary text-secondary-foreground rounded-t-3xl md:rounded-t-4xl border-t border-border/40 shadow-2xl">
        <div className="mx-auto max-w-7xl px-6 pt-16 pb-12 sm:px-8 lg:px-12">
          {/* Main Footer Grid */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8 pb-14">
            {/* Column 1: Brand & Mission */}
            <div className="lg:col-span-4 flex flex-col items-start pr-0 lg:pr-6">
              <Link to="/" className="inline-flex items-center gap-3.5 mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md group text-left">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" role="img" aria-label="Brookhaven Elevation Renovations logo" className="h-[60px] w-auto shrink-0">
                  <defs>
                    <linearGradient id="gL-f" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="#9fd3ff" stopOpacity=".85" />
                      <stop offset="1" stopColor="#1e3a5f" stopOpacity=".75" />
                    </linearGradient>
                    <linearGradient id="gR-f" x1="1" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#e9f5ff" stopOpacity=".9" />
                      <stop offset="1" stopColor="#5b8db8" stopOpacity=".6" />
                    </linearGradient>
                    <linearGradient id="gRoofL-f" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="#ffffff" stopOpacity=".95" />
                      <stop offset="1" stopColor="#b7dcf5" stopOpacity=".55" />
                    </linearGradient>
                    <linearGradient id="gRoofR-f" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="#d9eefc" stopOpacity=".8" />
                      <stop offset="1" stopColor="#3b6a94" stopOpacity=".55" />
                    </linearGradient>
                    <linearGradient id="cu-f" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0" stopColor="#f3c08a" />
                      <stop offset=".5" stopColor="#c8793a" />
                      <stop offset="1" stopColor="#8a4a1f" />
                    </linearGradient>
                    <linearGradient id="base-f" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#c8793a" stopOpacity=".35" />
                      <stop offset="1" stopColor="#c8793a" stopOpacity="0" />
                    </linearGradient>
                    <filter id="sh-f" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="4" stdDeviation="3" floodColor="#1e3a5f" floodOpacity=".25" />
                    </filter>
                  </defs>
                  <polygon points="60,104 18,80 60,56 102,80" fill="url(#base-f)" />
                  <g filter="url(#sh-f)">
                    <polygon points="22,58 60,80 60,104 22,82" fill="url(#gL-f)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
                    <polygon points="60,80 98,58 98,82 60,104" fill="url(#gR-f)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
                    <polygon points="34,74 44,80 44,96 34,90" fill="#1e3a5f" opacity=".55" />
                    <polygon points="70,82 86,73 86,84 70,93" fill="#ffffff" opacity=".7" stroke="#c8793a" strokeWidth="1" />
                    <polygon points="22,58 41,45 79,23 60,36" fill="url(#gRoofR-f)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
                    <polygon points="22,58 60,80 41,45" fill="url(#gL-f)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
                    <polygon points="60,80 98,58 79,23 41,45" fill="url(#gRoofL-f)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
                    <line x1="41" y1="45" x2="79" y2="23" stroke="url(#cu-f)" strokeWidth="3.2" strokeLinecap="round" />
                    <circle cx="41" cy="62" r="4" fill="#fff" opacity=".75" stroke="#c8793a" strokeWidth="1" />
                    <path d="M66 72 L84 40" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity=".8" />
                    <path d="M27 64 L27 78" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" opacity=".6" />
                  </g>
                  <circle cx="79" cy="23" r="2.6" fill="#f3c08a" />
                </svg>

                <div className="flex flex-col text-left">
                  <span className="font-serif text-lg font-semibold tracking-tight text-secondary-foreground leading-tight">
                    Brookhaven Elevation
                  </span>
                  <span className="text-[10px] font-semibold tracking-[0.25em] text-[#c8793a] uppercase leading-tight mt-0.5">
                    RENOVATIONS
                  </span>
                </div>
              </Link>

              <p className="text-secondary-foreground/80 text-sm leading-relaxed mb-6 font-default">
                Custom residential renovation and architectural remodeling in Brookhaven and North Atlanta. Dedicated project management, transparent scheduling, and enduring local craftsmanship.
              </p>
            </div>

            {/* Column 2: Contact Information */}
            <div className="lg:col-span-3 space-y-4">
              <h3 className="font-serif text-lg font-semibold tracking-tight text-secondary-foreground border-b border-primary/30 pb-2 inline-block">
                Contact
              </h3>

              <ul className="space-y-3.5 text-sm">
                <li className="flex items-center gap-3">
                  <Phone className="size-4 shrink-0 text-primary" />
                  <a href="tel:+14708479076" className="text-secondary-foreground/80 hover:text-primary transition-colors duration-180">
                    (470) 847-9076
                  </a>
                </li>

                <li className="flex items-center gap-3">
                  <Mail className="size-4 shrink-0 text-primary" />
                  <a href="mailto:serviceleadalerts@gmail.com" className="text-secondary-foreground/80 hover:text-primary transition-colors duration-180">
                    serviceleadalerts@gmail.com
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 3: Service Territories */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="font-serif text-lg font-semibold tracking-tight text-secondary-foreground border-b border-primary/30 pb-2 inline-block">
                Service Areas
              </h3>

              <ul className="space-y-2 text-sm text-secondary-foreground/80">
                {serviceAreas.map((area, index) => <li key={area} data-index={index} className="flex items-center gap-2 hover:text-secondary-foreground transition-colors duration-180">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/70 shrink-0" />
                    <Link to="#" className="hover:text-primary transition-colors duration-180">
                      {area}
                    </Link>
                  </li>)}
              </ul>
            </div>

            {/* Column 4: Quick Links & Social */}
            <div className="lg:col-span-3 space-y-4">
              <h3 className="font-serif text-lg font-semibold tracking-tight text-secondary-foreground border-b border-primary/30 pb-2 inline-block">
                Explore
              </h3>

              <MenuProvider menu_id="47" className="mb-6">
                <FooterMenuConsumer />
              </MenuProvider>

              <div className="pt-2">
                <span className="block text-xs uppercase tracking-widest text-secondary-foreground/60 mb-3">
                  Follow Our Work
                </span>
                <div className="flex items-center gap-2">
                  {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return <Button key={social.title} data-index={index} variant="outline" size="icon" asChild className="size-9 rounded-lg border-border/20 bg-secondary-foreground/5 text-secondary-foreground hover:text-primary hover:border-primary/50 hover:bg-secondary-foreground/10 transition-colors duration-180">
                        <Link to={social.href} aria-label={social.title}>
                          <IconComponent className="size-4" />
                        </Link>
                      </Button>;
                })}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Architectural Accent Hairline Divider */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

          {/* Bottom Bar: Copyright & Credentials */}
          <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-secondary-foreground/65 font-default">
            <p>
              {"©"} {new Date().getFullYear()} Brookhaven Elevation Renovations. All rights reserved.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-6 text-xs">
              <Link to="#" className="hover:text-primary transition-colors duration-180">
                Privacy Policy
              </Link>
              <span className="h-3 w-px bg-border/40" />
              <Link to="#" className="hover:text-primary transition-colors duration-180">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}